import Swiper from "swiper/swiper-bundle";
import GLightbox from "glightbox";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default {
  init() {
    const slideshow = new Swiper(".slideshow.swiper", {
      loop: true,
      navigation: {
        nextEl: "",
        prevEl: "",
        clickable: true,
      },
      autoplay: {
        delay: 4000,
      },
      speed: 3000,
    });

    const slideshowmobile = new Swiper(".slideshow-mobile.swiper", {
      loop: true,
      navigation: {
        nextEl: "",
        prevEl: "",
        clickable: true,
      },
      autoplay: {
        delay: 4000,
      },
      speed: 3000,
    });

    const testimonial = new Swiper(".testimonial-gallery .swiper", {
      slidesPerView: 1,
      breakpoints: {
        992: {
          slidesPerView: 3,
        },
        768: {
          slidesPerView: 1,
        },
      },
      slidesPerGroup: 1,
      spaceBetween: 50,
      navigation: {
        nextEl: ".testimonial-button-next",
        prevEl: ".testimonial-button-prev",
        clickable: true,
      },
      pagination: {
        el: ".swiper-pagination-home",
        clickable: true,
      },
    });

    const consignment = new Swiper(".consignment-gallery .swiper", {
      slidesPerView: 1,
      breakpoints: {
        992: {
          slidesPerView: 4,
        },
      },
      slidesPerGroup: 1,
      spaceBetween: 50,
      pagination: {
        el: ".swiper-pagination-consignment",
        clickable: true,
      },
    });

    const article = new Swiper(".article-gallery .swiper", {
      slidesPerView: 1,
      breakpoints: {
        992: {
          slidesPerView: 3,
          loop: false,
        },
        768: {
          slidesPerView: 2,
          loop: false,
        },
      },
      slidesPerGroup: 1,
      spaceBetween: 50,
      loop: true,
      pagination: {
        el: ".swiper-pagination-article",
        clickable: true,
      },
    });

    const lightbox = GLightbox({
      selector: ".glightbox",
    });

    this.setupCashContentToggle();
    this.setupHorizontalPanels();

    // Call the function to set up event listeners after all elements are loaded
    this.setupEventListeners();
  },
  finalize() {},

  setupEventListeners() {
    // Setup event listeners for tab navigation
    const tablinks = document.querySelectorAll(".tablinks");
    const tablinksCal = document.querySelectorAll(".tablinks-cal");
    tablinks.forEach((tablink) => {
      tablink.addEventListener("click", (event) => {
        const channelName = tablink.getAttribute("data-channel");
        this.openChannel(event, channelName);
      });
    });
    tablinksCal.forEach((tablink) => {
      tablink.addEventListener("click", (event) => {
        const channelName = tablink.getAttribute("data-channel");
        this.openChannelCal(event, channelName);
      });
    });
  },

  setupCashContentToggle() {
    const content = document.querySelector("[data-cash-content]");
    const toggle = document.querySelector("[data-cash-toggle]");

    if (!content || !toggle) {
      return;
    }

    const moreLabel = toggle.getAttribute("data-label-more") || "Read more";
    const lessLabel = toggle.getAttribute("data-label-less") || "Read less";
    const collapsedHeight = 400;
    const toggleLabel = toggle.querySelector("span");

    const setToggleLabel = (label) => {
      if (toggleLabel) {
        toggleLabel.textContent = label;
      } else {
        toggle.textContent = label;
      }
    };

    const syncState = () => {
      const isOverflowing = content.scrollHeight > collapsedHeight;

      toggle.classList.toggle("d-none", !isOverflowing);

      if (!isOverflowing) {
        content.classList.remove("is-expanded");
        toggle.setAttribute("aria-expanded", "false");
        setToggleLabel(moreLabel);
      }
    };

    toggle.addEventListener("click", () => {
      const isExpanded = content.classList.toggle("is-expanded");
      toggle.setAttribute("aria-expanded", String(isExpanded));
      setToggleLabel(isExpanded ? lessLabel : moreLabel);
    });

    syncState();
    window.addEventListener("resize", syncState);
  },

  setupHorizontalPanels() {
    const container = document.querySelector("[data-horizontal-scroll]");
    const track = document.querySelector("[data-horizontal-track]");

    if (!container || !track) {
      return;
    }

    const panels = gsap.utils.toArray(".home-cash-panel");

    if (panels.length < 2) {
      return;
    }

    const mm = gsap.matchMedia();

    mm.add("(min-width: 992px)", () => {
      gsap.set(track, { x: 0 });

      const header = document.querySelector(".site-header");
      const dotsContainer = container.querySelector("[data-horizontal-dots]");
      const dots = dotsContainer
        ? dotsContainer.querySelectorAll(".home-cash-horizontal__dot")
        : [];

      const updateDots = (index) => {
        dots.forEach((dot, i) =>
          dot.classList.toggle("is-active", i === index),
        );
      };

      const hideOverlays = () => {
        const form = document.querySelector(".post-property-form > form");
        gsap.to([header, form].filter(Boolean), {
          opacity: 0,
          duration: 0.3,
          onComplete() {
            gsap.set(this.targets(), { visibility: "hidden" });
          },
        });
        if (dotsContainer) dotsContainer.classList.add("is-pinned");
      };

      const showOverlays = () => {
        const form = document.querySelector(".post-property-form > form");
        gsap.to([header, form].filter(Boolean), {
          opacity: 1,
          visibility: "visible",
          duration: 0.3,
        });
        if (dotsContainer) dotsContainer.classList.remove("is-pinned");
      };

      // HOLD = สัดส่วน scroll ที่เป็น hold หลัง panel สุดท้าย (0.0–1.0)
      const HOLD = 0.1;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: () =>
            `+=${(track.scrollWidth - window.innerWidth) / (1 - HOLD * 2)}`,
          scrub: 1,
          pin: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const slideProgress = Math.max(
              0,
              Math.min((self.progress - HOLD) / (1 - HOLD * 2), 1),
            );
            updateDots(Math.round(slideProgress * (panels.length - 1)));
          },
          onEnter: hideOverlays,
          onLeave: showOverlays,
          onEnterBack: hideOverlays,
          onLeaveBack: showOverlays,
        },
      });

      tl.to({}, { duration: HOLD });
      tl.to(track, {
        x: () => -(track.scrollWidth - window.innerWidth),
        ease: "none",
        duration: 1 - HOLD * 2,
      });
      tl.to({}, { duration: HOLD });

      return () => {
        if (tl.scrollTrigger) {
          tl.scrollTrigger.kill();
        }
        tl.kill();
        gsap.set(track, { clearProps: "transform" });
      };
    });

    mm.add("(max-width: 991px)", () => {
      gsap.set(track, { clearProps: "transform" });
    });
  },

  openChannel(evt, channelName) {
    const tabcontent = document.querySelectorAll(".tabcontent");
    tabcontent.forEach((content) => {
      content.style.display = "none";
    });
    const tablinks = document.querySelectorAll(".tablinks");
    tablinks.forEach((link) => {
      link.classList.remove("channel-active");
    });
    document.getElementById(channelName).style.display = "block";
    evt.currentTarget.classList.add("channel-active");
  },

  openChannelCal(evt, channelName) {
    const tabcontent = document.querySelectorAll(".tabcontent-cal");
    tabcontent.forEach((content) => {
      content.style.display = "none";
    });
    const tablinks = document.querySelectorAll(".tablinks-cal");
    tablinks.forEach((link) => {
      link.classList.remove("channel-active-cal");
    });
    document.getElementById(channelName).style.display = "block";
    evt.currentTarget.classList.add("channel-active-cal");
  },
};
